/* ------------------------ Functional imports start ------------------------ */
import {
  DefinedUseQueryResult, keepPreviousData, QueryClient,
  QueryFunction, QueryFunctionContext, QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult
} from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react";
import { DetailQueryParameters, type PaginatedQueryParameters } from "./utils/types";
import { PaginatedResponse, type DetailResponse } from "../client";
import { availableTablePageSizes } from "../../config/ui";
import QueryKeyFactory from "./utils/queryKeyFactory";
import { EntitySearchResponse } from "../../hooks/useEntitySearch";
import { EntityBaseType } from "../types";
import { getPKfromSelf } from "../../utils/functions";
/* ------------------------- Functional imports end ------------------------- */

/* ------------------------- Type definitions start ------------------------- */
/**
 * useSmartQuery hook options for detail queries.
 */
export interface UseSmartQueryDetailOptions<TEntity>
  extends Omit<UseQueryOptions<DetailResponse<TEntity>>, 'queryKey' | 'queryFn'>
{
  queryKeyFactory: QueryKeyFactory,
  queryFn:  (context: QueryFunctionContext, id: number) => DetailResponse<TEntity> | Promise<DetailResponse<TEntity>>,
  queryParameters: DetailQueryParameters<TEntity>
}

/**
 * useSmartQuery hook options for paginated (list) queries.
 */
export interface UseSmartQueryListOptions<TEntity, TFilters = unknown>
  extends Omit<UseQueryOptions<PaginatedResponse<TEntity>>, 'queryKey' | 'queryFn'>
{
  queryKeyFactory: QueryKeyFactory,
  queryFn: (context: QueryFunctionContext, ids?: number[]) => PaginatedResponse<TEntity> | Promise<PaginatedResponse<TEntity>>,
  elasticSearchFn: QueryFunction<EntitySearchResponse<TEntity>>,
  queryParameters: PaginatedQueryParameters<TEntity, TFilters>
}
/* -------------------------- Type definitions end -------------------------- */


/* ------------------------ Utility functions start ------------------------- */
/**
 * Generator function that yields all possible combinations of values in an array.
 *
 * Caution:
 *  Use sparingly, as the number of combinations grows exponentially with the
 *  size of the input array. An array of length N will yield 2^N combinations
 *  (and therefore 2^N iterations of any loop that is implemented based on the
 *  combinations).
 *
 * How to use:
 *  for(const combination of getCombinations([1, 2, 3])) {
 *    // ... do something with the yielded combination
 *  }
 *
 * @param values Array<T> - The array of values to generate combinations for.
 * @param index number - The index is used internally to specify the state of the
 *                       recursion. Do not manually set a value!
 */
function* getCombinations<T>(values: T[], index = 0): Generator<T[], T[] | undefined> {
  // Base case: When we reach the end of the array, yield an empty set.
  if (index === values.length) {
    yield [];
    return;
  }
  // Extract the current element we are processing
  const current = values[index]
  // Recursively generate combinations for the remaining elements
  for (const combination of getCombinations(values, index + 1)) {
    // Case 1: Yield the combination WITHOUT the current element
    yield combination

    // Case 2: Yield the combination WITH the current element
    yield [current, ...combination]
  }
}

/**
 * Sets the detail cache for the specified parameters using the specified instance.
 * @param queryKeyFactory
 * @param parameters
 * @param instance
 * @param queryClient
 */
function setDetailCache<TEntity extends { self: string, id?: string | number }>(
  queryKeyFactory: QueryKeyFactory,
  parameters: DetailQueryParameters<TEntity>,
  instance: TEntity,
  queryClient: QueryClient,
): void {
  const instancePk = instance['id'] ?? getPKfromSelf(instance.self)
  const queryKey = queryKeyFactory.detail(
    Number(instancePk),
    parameters,
  )
  queryClient.setQueryData(
    queryKey,
    instance,
    {updatedAt: Date.now()},
  )
}

/**
 * Populates as many detail cache entries as viable using the specified instance.
 * This is enabled by the fact that an instance with expanded properties also satisfies
 * the data requirements for queries that specify fewer/no expand parameters.
 * @param queryKeyFactory
 * @param parameters
 * @param instance
 * @param queryClient
 */
function populateDetailCaches<TEntity extends { self: string, id?: string | number }>(
  queryKeyFactory: QueryKeyFactory,
  parameters: DetailQueryParameters<TEntity>,
  instance: TEntity,
  queryClient: QueryClient,
): void {
  if(Array.isArray(parameters.expand)) {
    for(const combination of getCombinations(parameters.expand)) {
      setDetailCache(
        queryKeyFactory,
        {...parameters, expand: combination},
        instance,
        queryClient
      )
    }
  } else {
    // no expand parameters -> nothing smart to do. Just call setDetailCache
    setDetailCache(queryKeyFactory, parameters, instance, queryClient)
  }
}

/**
 * Sets the list cache for the specified parameters using the specified PaginatedResponse.
 * This is enabled by the fact that a page of instances with expanded properties
 * also satisfies the data requirements for queries that specify fewer/no expand
 * parameters.
 * @param queryKeyFactory
 * @param parameters
 * @param page
 * @param queryClient
 */
function setListCache<
  TEntity extends { self: string, id?: string | number },
  TFilters,
>(
  queryKeyFactory: QueryKeyFactory,
  parameters: PaginatedQueryParameters<TEntity, TFilters>,
  page: PaginatedResponse<TEntity>,
  queryClient: QueryClient,
) {
  const queryKey = queryKeyFactory.list(parameters)
  queryClient.setQueryData(
    queryKey,
    page,
    {updatedAt: Date.now()},
  )
}

/**
 * Populates as many list cache entries as viable using the specified parameters
 * and PaginatedResponse.
 * @param queryKeyFactory
 * @param parameters
 * @param page
 * @param queryClient
 */
function populateListCaches<
  TEntity extends {self: string, id?: string | number},
  TFilters,
>(
  queryKeyFactory: QueryKeyFactory,
  parameters: PaginatedQueryParameters<TEntity, TFilters>,
  page: PaginatedResponse<TEntity>,
  queryClient: QueryClient,
) {
  // populate list cache for lists with fewer expand parameters
  if(Array.isArray(parameters.expand)) {
    for(const combination of getCombinations(parameters.expand)) {
      setListCache<TEntity, TFilters>(
        queryKeyFactory,
        {...parameters, expand: combination},
        page,
        queryClient,
      )
    }
  }

  // populate list caches for smaller page sizes
  if(parameters.page === 1
    && parameters.pageSize
    && parameters.pageSize > 10
  ) {
    // populate the query cache for smaller page sizes
    const smallerPageSizes = availableTablePageSizes
      .filter(size => size < parameters.pageSize!)
    for(const pageSize of smallerPageSizes) {
      const updatedQueryParams: PaginatedQueryParameters<TEntity, TFilters> = {
        ...parameters,
        pageSize: pageSize,
      }
      setListCache<TEntity, TFilters>(
        queryKeyFactory,
        updatedQueryParams,
        {
          ...page,
          results: page.results.slice(0, pageSize),
        },
        queryClient,
      )
    }
  }
}
/* ------------------------- Utility functions end -------------------------- */



/* -------------------------------------------------------------------------- */
/*                                Export hooks                                */
/* -------------------------------------------------------------------------- */
export function useSmartListQuery<
  TEntity extends EntityBaseType,
  TFilters,
>(
  options: UseSmartQueryListOptions<TEntity, TFilters>,
  enabled=true,
  idsOrUrls?: number[] | string[]
): UseQueryResult<PaginatedResponse<TEntity>> {
  /* ------------------------- Non state data start ------------------------- */
  const queryClient = useQueryClient()
  /* -------------------------- Non state data end -------------------------- */

  /* -------------------------- Flag states start --------------------------- */
  const isSearchQuery: boolean = (
    options.queryParameters.filters !== undefined
    && options.queryParameters.filters !== null
    && Object.keys(options.queryParameters.filters).length > 0
  )
  const buildEmptyPage = () => {
    const page = options.queryParameters.page ?? 1
    const pageSize = options.queryParameters.pageSize ?? availableTablePageSizes[0]
    return {
      count: 0,
      next: null,
      previous: null,
      page,
      page_size: pageSize,
      results: [],
    }
  }
  /* --------------------------- Flag states end ---------------------------- */

  /* -------------------------- Data states start --------------------------- */
  // Note: managedIds could also live in a useRef, but we use a useState here to
  // trigger the hook to re-run whenever the managedIds are changed. This enables
  // us to run the search and fetch queries sequentially.
  const [managedIds, setManagedIds] = useState<number[] | undefined>(
    typeof idsOrUrls === 'undefined' || idsOrUrls.length === 0
      ? undefined
      // received an array specifying instances
      : (idsOrUrls.every((id: string | number) => typeof id === 'string')
        // instances are specified using instance self-urls -> convert to ids
        ? idsOrUrls.map((self: string) => getPKfromSelf(self) as unknown as number)
        // instances are specified using ids -> use as provided
        : idsOrUrls
      )
  )
  // if the query is a search query we need to manually manage the query parameters
  // for the fetch query, as the filter parameters that were provided with the hook
  // call are 'used up' by the search query and not relevant for the fetch query.
  const managedFetchQueryParams = useRef<
    PaginatedQueryParameters<TEntity, TFilters> | undefined
  >(undefined)

  const search = useQuery({
    queryKey: options.queryKeyFactory.search(options.queryParameters),
    queryFn: options.elasticSearchFn,
    enabled: enabled && isSearchQuery,
  })
  const query = useQuery({
    queryKey: options.queryKeyFactory.list(
      managedFetchQueryParams.current ?? options.queryParameters,
      managedIds
    ),
    queryFn: async (context: QueryFunctionContext<QueryKey>) => {
      // For search queries, return an empty page while waiting for search results or when no ids were found
      if (isSearchQuery) {
        if (search.status !== 'success') {
          return buildEmptyPage()
        }
        if (Array.isArray(managedIds) && managedIds.length === 0) {
          return buildEmptyPage()
        }
      }

      return options.queryFn(context, managedIds)
    },
    placeholderData: isSearchQuery ? undefined : (options.placeholderData ?? keepPreviousData),
    enabled: enabled && (
      !isSearchQuery
      || search.status !== 'error'
    ),
  }, queryClient)

  const shouldForceEmptyResults =
    isSearchQuery
    && search.status === 'success'
    && Array.isArray(managedIds)
    && managedIds.length === 0
  let queryResult: UseQueryResult<PaginatedResponse<TEntity>> = query
  if (shouldForceEmptyResults && query.status === 'success') {
    queryResult = {
      ...query,
      data: buildEmptyPage(),
      isPlaceholderData: false,
    }
  }
  /* --------------------------- Data states end ---------------------------- */


  /* ---------------------------- Effects start ----------------------------- */
  // Reset managedIds when search is no longer active (filters cleared)
  // This ensures the query fetches all items again instead of the old filtered IDs
  // Only reset if no external idsOrUrls were provided
  const hasExternalIds = idsOrUrls !== undefined && idsOrUrls.length > 0
  useEffect(() => {
    if (!isSearchQuery && !hasExternalIds) {
      setManagedIds(undefined)
      managedFetchQueryParams.current = undefined
    }
  }, [isSearchQuery, hasExternalIds])

  // TODO: move side effects into onSuccess callback
  useEffect(() => {
    if(search.status === 'success') {
      const searchResponse: EntitySearchResponse<any> = search.data
      const ids = searchResponse.results.map((result) => result.id)
      setManagedIds(ids)
      managedFetchQueryParams.current = {
        ...options.queryParameters,
        filters: undefined,
        filterChainingOperator: undefined,
      }
    }
  }, [search.status])

  // subscribe to changes of the query status
  // TODO: move side effects into onSuccess callback
  useEffect(() => {
    if(queryResult.status === 'success') {
      // success fetching data from the backend. We received a PaginatedResponse
      // -> try to use that data in a smart way such that we can minimize the number
      // of future backend accesses to optimize performance, while also making
      // sure that we always display reasonably up-to-date data.

      // populate detail caches using the instances from the list query
      const instances: TEntity[] = queryResult.data.results
      const detailQueryParameters = options.queryParameters as DetailQueryParameters<TEntity>
      for (const instance of instances) {
        populateDetailCaches(
          options.queryKeyFactory,
          detailQueryParameters,
          instance,
          queryClient,
        )
      }

      // populate list caches using the page that we received
      populateListCaches(
        options.queryKeyFactory,
        options.queryParameters,
        queryResult.data,
        queryClient,
      )
    }
  }, [queryResult.status])
  /* ----------------------------- Effects end ------------------------------ */

  return queryResult
}

export function useSmartDetailQuery<TEntity extends EntityBaseType>(
  idOrUrl: number | string,
  options: UseSmartQueryDetailOptions<TEntity>,
  enabled=true,
): UseQueryResult<DetailResponse<TEntity>> {
  /* ------------------------- Non state data start ------------------------- */
  const queryClient = useQueryClient()
  const id = typeof idOrUrl === 'string'
    ? Number(getPKfromSelf(idOrUrl))
    : idOrUrl
  /* -------------------------- Non state data end -------------------------- */

  /* -------------------------- Data states start --------------------------- */
  const query = useQuery({
    queryKey: options.queryKeyFactory.detail(id, options.queryParameters),
    queryFn: (context: QueryFunctionContext<QueryKey>) => options.queryFn(context, id),
    placeholderData: options.placeholderData ?? keepPreviousData,
    enabled: enabled,
  }, queryClient)
  /* --------------------------- Data states end ---------------------------- */

  /* ---------------------------- Effects start ----------------------------- */
  // subscribe to changes of the query status
  // TODO: move side effects into onSuccess callback
  useEffect(() => {
    if(query.status === 'success') {
      // success fetching a single instance from backend -> try to use that data
      // in a smart way such that we can minimize the number of future backend accesses,
      // while also making sure that we always display reasonably up-to-date data.
      const instance: TEntity = query.data
      populateDetailCaches(
        options.queryKeyFactory,
        options.queryParameters,
        instance,
        queryClient,
      )
    }
  }, [query.status])
  /* ----------------------------- Effects end ------------------------------ */

  return query
}
